import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ActivatedRoute} from '@angular/router';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {marked} from 'marked';

interface TocItem {
    id: string;
    text: string;
    level: number;
}

/**
 * マニュアル表示ページ。src/assets/manuals/<file>.md を取得し marked で HTML 化して表示する。
 * ルート data で file / title / pub(公開か) を受け取る。ユーザ用は公開ルート(AuthGuard 無し)、
 * 管理者用・開発者用は AuthGuard 配下に置く (app.routing.ts)。画像は assets/manuals/img/ を参照。
 *
 * <p>デザイン: 見出しから目次(TOC)を生成しサイドバーに表示、見出しにアンカーを付与、画像を
 * figure/figcaption 化、引用をコールアウト化。スクロールで現在地の目次項目を強調する。
 */
@Component({
    standalone: false,
    selector: 'app-manual',
    templateUrl: 'manual.component.html',
    styleUrls: ['manual.component.css'],
    // [innerHTML] で挿入した Markdown 由来の要素にもスタイルを効かせるため無効化。
    // セレクタはすべて .manual-* 配下に限定しているのでグローバル汚染はしない。
    encapsulation: ViewEncapsulation.None,
})
export class ManualComponent implements OnInit, AfterViewInit, OnDestroy {
    /** 翻訳キー (route.* / shell.manual)。テンプレートで transloco を通す。 */
    title = 'shell.manual';
    subtitle = '';
    html: SafeHtml = '';
    toc: TocItem[] = [];
    activeId = '';
    loading = true;
    error = false;
    isPublic = false;
    tocOpen = false;

    @ViewChild('body') bodyRef?: ElementRef<HTMLElement>;
    private observer?: IntersectionObserver;

    constructor(private http: HttpClient, private route: ActivatedRoute, private sanitizer: DomSanitizer) {
    }

    ngOnInit(): void {
        const data: any = this.route.snapshot.data || {};
        this.title = data.title || 'shell.manual';
        this.isPublic = !!data.pub;
        this.subtitle = this.isPublic ? 'shell.manual_public' : 'shell.manual_after_login';
        const file = data.file;
        if (!file) {
            this.error = true;
            this.loading = false;
            return;
        }
        this.http.get('assets/manuals/' + file, {responseType: 'text'}).subscribe({
            next: (md: string) => {
                this.render(md);
                this.loading = false;
                // 描画反映後に見出しを監視して目次の現在地を強調する。
                setTimeout(() => this.observeHeadings(), 0);
            },
            error: () => {
                this.error = true;
                this.loading = false;
            },
        });
    }

    ngAfterViewInit(): void {
        // render は非同期(HTTP)後なので、監視は render 内の setTimeout で開始する。
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    /** Markdown を HTML 化し、見出しに id 付与・目次生成・画像の figure 化を行う。 */
    private render(md: string): void {
        const rendered = marked.parse(md) as string;
        const doc = new DOMParser().parseFromString(rendered, 'text/html');

        // 見出し(h2/h3)に id を付け目次を作る。
        const toc: TocItem[] = [];
        let n = 0;
        doc.querySelectorAll('h2, h3').forEach((h) => {
            const id = 'sec-' + (++n);
            h.setAttribute('id', id);
            toc.push({id, text: h.textContent || '', level: h.tagName === 'H3' ? 3 : 2});
        });
        this.toc = toc;

        // 画像を figure/figcaption 化 (alt をキャプションに)。marked は画像を <p><img></p> で
        // 出力するため、画像だけの段落は figure で置換し (p の中に figure を入れない)、
        // 段落内に文が混在する場合はその場で figure に包む。
        Array.from(doc.querySelectorAll('img')).forEach((img) => {
            const alt = img.getAttribute('alt') || '';
            img.setAttribute('loading', 'lazy');
            const fig = doc.createElement('figure');
            fig.className = 'manual-figure';
            const p = img.parentElement;
            const soleInParagraph = p && p.tagName === 'P' && (p.textContent || '').trim() === '' && p.querySelectorAll('img').length === 1;
            if (soleInParagraph && p.parentNode) {
                p.parentNode.replaceChild(fig, p);
            } else if (img.parentNode) {
                img.parentNode.replaceChild(fig, img);
            } else {
                return;
            }
            fig.appendChild(img);
            if (alt) {
                const cap = doc.createElement('figcaption');
                cap.textContent = alt;
                fig.appendChild(cap);
            }
        });

        this.html = this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
    }

    /** 目次クリックで該当見出しへスムーズスクロール。 */
    scrollTo(id: string, ev?: Event): void {
        if (ev) {
            ev.preventDefault();
        }
        this.tocOpen = false;
        const el = this.bodyRef?.nativeElement.querySelector('#' + id) as HTMLElement | null;
        if (el) {
            el.scrollIntoView({behavior: 'smooth', block: 'start'});
            this.activeId = id;
        }
    }

    /** スクロール中の現在セクションを目次に反映する。 */
    private observeHeadings(): void {
        if (!this.bodyRef || typeof IntersectionObserver === 'undefined') {
            return;
        }
        const heads = Array.from(this.bodyRef.nativeElement.querySelectorAll('h2, h3'));
        if (!heads.length) {
            return;
        }
        this.observer = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (e.isIntersecting) {
                    this.activeId = (e.target as HTMLElement).id;
                }
            }
        }, {rootMargin: '-80px 0px -70% 0px', threshold: 0});
        heads.forEach((h) => this.observer!.observe(h));
    }
}
